import React, { FC, useEffect, useState, useCallback, useRef } from 'react';
import { Button, Table, Dropdown, Select, PaginationProps, message } from 'antd';
import { LeftOutlined, LoadingOutlined, MoreOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import Main from 'components/layout/Main';
import TextInput from 'components/Elements/TextInput';
import { SearchIcon, ChevronDownIcon } from 'components/Elements/CustomSVGIcon';
import { CombinedStates, AppDispatch } from 'core/types';
import { ADMIN_ACCESS_LEVELS } from 'core/utils/value';
import { requestFormRoute } from 'core/utils/requestFormRoute';
import { fetchSites, fetchLastBookedSites } from 'core/sites/sitesApi';
import { createVisitRequest } from 'core/visitRequestForm/visitRequestFormApi';
import { createAccessRequest } from 'core/accessRequestForm/accessRequestFormApi';
import { createGuardingRequest } from 'core/guardingRequestForm/guardingRequestFormApi';
import { resubmitActionRequest } from 'core/visitRequestReview/visitRequestReviewApi';
import { SiteData, RecentVisitRequest } from 'core/sites/types';
import { buildMainToolbar } from 'core/utils/mainToolbar';
import FtcReportModal from './components/FtcReportModal';
import FtcUpdateModal from './components/FtcUpdateModal';
import ProjectGuardingModal from './components/ProjectGuardingModal';
import './sites.scss';

/**
 * Sort field for the "Site" column. The cell displays the combined label
 * `${type}: ${name}` (e.g. "DC: Basingstoke Data Centre"), so sorting by the
 * bare `name` would order rows in a way that doesn't match what's on screen.
 * We send this dedicated field instead; the backend must order by the same
 * displayed value (i.e. ORDER BY type, name) so the visible order is correct.
 */
const SITE_NAME_SORT_FIELD = 'typeName';

const Sites: FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { userData } = useSelector((state: CombinedStates) => state.user);
    const { sitesData, isFetching, pagination, filterData, recentRequests } = useSelector((state: CombinedStates) => state.sites);

    const [searchText, setSearchText] = useState<string>('');
    const [selectedSiteType, setSelectedSiteType] = useState<string[]>([]);
    const [selectedSite, setSelectedSite] = useState<string[]>([]);
    const [sortDirection, setSortDirection] = useState<string | null>(null);
    const [sortField, setSortField] = useState<string | null>(null);
    const [showInfoBanner, setShowInfoBanner] = useState<boolean>(true);
    const [siteTypeDropdownOpen, setSiteTypeDropdownOpen] = useState<boolean>(false);
    const [siteDropdownOpen, setSiteDropdownOpen] = useState<boolean>(false);
    const [ftcReportVisible, setFtcReportVisible] = useState<boolean>(false);
    const [ftcUpdateVisible, setFtcUpdateVisible] = useState<boolean>(false);
    const [projectGuardingVisible, setProjectGuardingVisible] = useState<boolean>(false);
    const sitesTableRef = useRef<HTMLDivElement>(null);
    // The site a booking is being created for. Creating the draft can take a
    // moment, and users who saw nothing happen clicked again, which made a
    // draft per click. While one is in flight every row's menu is disabled and
    // that row's button shows a spinner. The ref closes the gap before React
    // re-renders, so a fast double click still only books once.
    const [bookingSiteId, setBookingSiteId] = useState<number | null>(null);
    const bookingInFlight = useRef(false);

    const toolbar = buildMainToolbar(userData?.role?.accessLevel);

    const navigation = [{ label: 'Item 1', link: '/abc' }, { label: 'Item 1', link: '/abc' }];
    const backLink = (
        <Button type="link" onClick={() => navigate(-1)}>
            <LeftOutlined /> BACK
        </Button>
    );

    const handleFetchSites = useCallback((page = 1) => {
        dispatch(fetchSites({
            q: searchText || undefined,
            site: selectedSite.length > 0 ? selectedSite : undefined,
            siteType: selectedSiteType.length > 0 ? selectedSiteType : undefined,
            page,
            sort: sortDirection,
            sortField,
        }));
    }, [dispatch, searchText, selectedSite, selectedSiteType, sortDirection, sortField]);

    const debouncedFetchSites = useCallback(debounce(() => {
        handleFetchSites(1);
    }, 500), [handleFetchSites]);

    /**
     * Initial data load on component mount.
     *
     * INTENTIONALLY uses empty dependency array to run ONLY ONCE on mount.
     *
     * Why we suppress exhaustive-deps:
     * - handleFetchSites is intentionally excluded because it changes when filter
     *   state changes, which would cause unwanted refetches on every filter change
     * - Filter-based refetching is handled by the debounced effect below
     * - dispatch is stable from useDispatch and technically safe to include,
     *   but excluding it here doesn't affect behavior
     * - fetchLastBookedSites only needs to run once on initial load
     */
    useEffect(() => {
        handleFetchSites(1);
        dispatch(fetchLastBookedSites());
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentional: mount-only effect
    }, []);

    /**
     * Debounced search/filter effect.
     *
     * Triggers a debounced search when any filter parameter changes.
     *
     * INTENTIONALLY excludes debouncedFetchSites from dependencies:
     * - debouncedFetchSites is recreated when handleFetchSites changes
     * - handleFetchSites already depends on the filter state
     * - Including debouncedFetchSites would cause redundant effect triggers
     * - We explicitly list filter parameters to make the trigger conditions clear
     * - The cleanup cancels pending debounced calls when deps change
     */
    useEffect(() => {
        debouncedFetchSites();
        return () => {
            debouncedFetchSites.cancel();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentional: debouncedFetchSites recreates on filter changes
    }, [searchText, selectedSite, selectedSiteType, sortDirection, sortField]);

    const handleClearAll = () => {
        setSearchText('');
        setSelectedSiteType([]);
        setSelectedSite([]);
        setSortDirection(null);
        setSortField(null);
    };

    const onColumnSort = (field: string) => {
        let newDirection: string | null = 'asc';
        if (sortField === field) {
            if (sortDirection === 'asc') {
                newDirection = 'desc';
            } else if (sortDirection === 'desc') {
                newDirection = null;
                setSortField(null);
                setSortDirection(null);
                return;
            }
        }
        setSortField(field);
        setSortDirection(newDirection);
    };

    const renderSortableHeader = (title: string, field: string) => (
        <div
            onClick={() => onColumnSort(field)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
            className="sortable-header">
            {title}
            <span className="sort-indicator">
                {sortField === field && (
                    sortDirection === 'asc' ? ' \u2191' : ' \u2193'
                )}
            </span>
        </div>
    );

    const runBooking = useCallback(async (siteId: number, book: () => Promise<void>) => {
        if (bookingInFlight.current) {
            return;
        }
        bookingInFlight.current = true;
        setBookingSiteId(siteId);
        try {
            await book();
        } finally {
            bookingInFlight.current = false;
            setBookingSiteId(null);
        }
    }, []);

    const handleBookVisitorRequest = useCallback((siteId: number) => runBooking(siteId, async () => {
        try {
            const resultAction = await dispatch(createVisitRequest({ siteId }));
            if (createVisitRequest.fulfilled.match(resultAction)) {
                const { payload } = resultAction;
                if (payload?.visitRequest?.encryptedId) {
                    // Skip step 1 (site selection) and go to step 2 (date selection)
                    navigate(`/visitor-request/${payload.visitRequest.encryptedId}/step/2`);
                }
            } else if (createVisitRequest.rejected.match(resultAction)) {
                message.error('Failed to create visit request. Please try again.');
            }
        } catch {
            message.error('An unexpected error occurred. Please try again.');
        }
    }), [dispatch, navigate, runBooking]);

    const handleBookGeneralAccess = useCallback((siteId: number) => runBooking(siteId, async () => {
        try {
            const resultAction = await dispatch(createAccessRequest({ siteId }));
            if (createAccessRequest.fulfilled.match(resultAction)) {
                const { payload } = resultAction;
                if (payload?.visitRequest?.encryptedId) {
                    // Retail general access starts on Date (step 2); Job Details moves after Visitors.
                    const firstStep = payload.stepData?.isRetail ? 2 : 1;
                    navigate(`/access-request/${payload.visitRequest.encryptedId}/step/${firstStep}`);
                }
            } else if (createAccessRequest.rejected.match(resultAction)) {
                message.error('Failed to create access request. Please try again.');
            }
        } catch {
            message.error('An unexpected error occurred. Please try again.');
        }
    }), [dispatch, navigate, runBooking]);

    const handleBookGuarding = useCallback((siteId: number) => runBooking(siteId, async () => {
        try {
            const resultAction = await dispatch(createGuardingRequest({ siteId }));
            if (createGuardingRequest.fulfilled.match(resultAction)) {
                const { payload } = resultAction;
                if (payload?.visitRequest?.encryptedId) {
                    // Retail guarding starts on Date (step 2); Job Details moves after Visitors.
                    const firstStep = payload.stepData?.isRetail ? 2 : 1;
                    navigate(`/guarding-request/${payload.visitRequest.encryptedId}/step/${firstStep}`);
                }
            } else if (createGuardingRequest.rejected.match(resultAction)) {
                message.error('Failed to create guarding request. Please try again.');
            }
        } catch {
            message.error('An unexpected error occurred. Please try again.');
        }
    }), [dispatch, navigate, runBooking]);

    const handleResubmitRequest = useCallback(async (encryptedId: string) => {
        try {
            const resultAction = await dispatch(resubmitActionRequest({ id: encryptedId }));
            if (resubmitActionRequest.fulfilled.match(resultAction)) {
                const { payload } = resultAction;
                if (payload?.visitRequestId) {
                    // Route by the resubmitted request's own type — a guarding
                    // request used to fall into the visitor branch here and open
                    // the wrong form.
                    const { formPath, step } = requestFormRoute(payload.type, payload.isRetail);
                    navigate(`/${formPath}/${payload.visitRequestId}/step/${step}`);
                }
            } else if (resubmitActionRequest.rejected.match(resultAction)) {
                message.error('Failed to resubmit request. Please try again.');
            }
        } catch {
            message.error('An unexpected error occurred. Please try again.');
        }
    }, [dispatch, navigate]);

    /**
     * `accessBookingAvailable` is required, not defaulted: the API always sends it
     * (SiteListResource / LastBookedSiteResource cast it to a bool), but the `sites`
     * slice is persisted to localStorage, so a store written before the field
     * existed rehydrates rows without it. A `= true` default turned that missing
     * value into an ENABLED "Book General Access" on a site with booking switched
     * off — so callers coerce, and the gate fails closed.
     */
    const getBookingMenuItems = (siteId: number, encryptedSiteId: string, isVisitorRequest: boolean, accessBookingAvailable: boolean) => {
        if (isVisitorRequest) {
            return [
                {
                    key: 'book-visitor',
                    label: 'Book Visitor Request',
                    onClick: () => handleBookVisitorRequest(siteId),
                    disabled: bookingSiteId !== null,
                },
            ];
        }
        return [
            {
                key: 'book-general',
                label: 'Book General Access',
                onClick: () => handleBookGeneralAccess(siteId),
                disabled: !accessBookingAvailable || bookingSiteId !== null,
            },
            {
                key: 'book-guarding',
                label: 'Book Guarding',
                onClick: () => handleBookGuarding(siteId),
                disabled: bookingSiteId !== null,
            },
        ];
    };

    // Incident creation is authorised for admins only — mirrors the backend
    // IncidentStoreRequest::authorize() (isAdmin || isSuperAdmin || isScorchsoftAdmin).
    // Security officers can view incidents but cannot create them, so don't offer
    // the action to them (the API would 403).
    const canReportIncident = ADMIN_ACCESS_LEVELS.includes(userData?.role?.accessLevel);

    const getActionMenuItems = (record: SiteData) => [
        {
            key: 'view',
            label: 'View Site Details',
            onClick: () => navigate(`/site/${record.encryptedId}`),
        },
        ...(canReportIncident ? [{
            key: 'report-incident',
            label: 'Report Incident',
            onClick: () => navigate(`/site/${record.encryptedId}/incidents`, {
                state: { siteName: record.name, siteId: String(record.id), openReport: true },
            }),
        }] : []),
        // !! on a typed-boolean field: guards rehydrated rows that predate it.
        ...getBookingMenuItems(record.id, record.encryptedId, record.isVisitorRequest, !!record.accessBookingAvailable),
    ];

    const getRecentRequestMenuItems = (record: RecentVisitRequest) => [
        {
            key: 'view',
            label: 'View Site Details',
            onClick: () => navigate(`/site/${record.siteEncryptedId}`),
        },
        {
            key: 'resubmit',
            label: 'Resubmit Request',
            onClick: () => handleResubmitRequest(record.encryptedId),
        },
        ...getBookingMenuItems(record.siteId, record.siteEncryptedId, record.isVisitorRequest, !!record.accessBookingAvailable),
    ];

    const recentRequestColumns = [
        {
            title: 'Site',
            dataIndex: 'siteName',
            key: 'siteName',
            render: (siteName: string | null) => siteName || '-',
        },
        {
            title: 'Access Type',
            dataIndex: 'accessType',
            key: 'accessType',
            render: (accessType: string | null) => accessType || '-',
        },
        {
            title: 'Work Type',
            dataIndex: 'workType',
            key: 'workType',
            render: (workType: string | null) => workType || '-',
        },
        {
            title: 'Date Requested',
            dataIndex: 'dateRequested',
            key: 'dateRequested',
            render: (dateRequested: string | null) => dateRequested || '-',
        },
        {
            title: 'Num of Visitors',
            dataIndex: 'numOfVisitors',
            key: 'numOfVisitors',
        },
        {
            title: ' ',
            key: 'actions',
            width: 60,
            render: (_: undefined, record: RecentVisitRequest) => (
                <Dropdown
                    menu={{ items: getRecentRequestMenuItems(record) }}
                    trigger={['click']}
                    placement="bottomRight"
                    disabled={bookingSiteId !== null}>
                    <Button
                        type="text"
                        className="action-button"
                        disabled={bookingSiteId !== null}
                        aria-busy={bookingSiteId === record.siteId}
                        aria-label={bookingSiteId === record.siteId ? 'Booking...' : 'Actions'}>
                        {bookingSiteId === record.siteId
                            ? <LoadingOutlined style={{ fontSize: 20 }} />
                            : <MoreOutlined style={{ fontSize: 20 }} />}
                    </Button>
                </Dropdown>
            ),
        },
    ];

    const tableColumns = [
        {
            title: renderSortableHeader('Site Code', 'siteCode'),
            dataIndex: 'siteCode',
            key: 'siteCode',
            width: 120,
            render: (siteCode: string | null) => siteCode || '-',
        },
        {
            title: renderSortableHeader('Site', SITE_NAME_SORT_FIELD),
            dataIndex: 'name',
            key: 'name',
            width: 220,
            render: (name: string, record: SiteData) => (record.type ? `${record.type}: ${name}` : name),
        },
        {
            title: renderSortableHeader('Address', 'fullAddress'),
            dataIndex: 'fullAddress',
            key: 'fullAddress',
            width: 350,
        },
        {
            title: renderSortableHeader('Access Type', 'accessType'),
            dataIndex: 'accessType',
            key: 'accessType',
            width: 150,
            render: (accessType: string | null) => accessType || '-',
        },
        {
            title: ' ',
            key: 'actions',
            width: 60,
            render: (_: undefined, record: SiteData) => (
                <Dropdown
                    menu={{ items: getActionMenuItems(record) }}
                    trigger={['click']}
                    placement="bottomRight"
                    disabled={bookingSiteId !== null}>
                    <Button
                        type="text"
                        className="action-button"
                        disabled={bookingSiteId !== null}
                        aria-busy={bookingSiteId === record.id}
                        aria-label={bookingSiteId === record.id ? 'Booking...' : 'Actions'}>
                        {bookingSiteId === record.id
                            ? <LoadingOutlined style={{ fontSize: 20 }} />
                            : <MoreOutlined style={{ fontSize: 20 }} />}
                    </Button>
                </Dropdown>
            ),
        },
    ];

    const handleTotal = (total: number, range: [number, number]): JSX.Element => (
        <div style={{ left: 0, position: 'absolute' }}>
            Results: <span style={{ color: '#EF233C' }}>{range[0].toLocaleString()} - {range[1].toLocaleString()} of {pagination.total.toLocaleString()}</span>
        </div>
    );

    const handlePageChange = (page: number) => {
        handleFetchSites(page);
        sitesTableRef.current?.scrollIntoView({ block: 'start', behavior: 'auto' });
    };

    const itemRender: PaginationProps['itemRender'] = (page, type, originalElement) => {
        const lastPage = Math.ceil(pagination.total / pagination.pageSize);
        if (type === 'page') {
            return page === pagination.currentPage ? (
                <div className="sites-table-pagination">
                    Page <span style={{ color: '#EF233C' }}>{page}</span> of {lastPage}
                </div>
            ) : null;
        }
        return originalElement;
    };

    // Placeholder options for filters - will be populated from API later
    const siteTypeOptions = filterData.siteTypes?.map(type => ({
        value: type,
        label: type,
    })) || [];

    const siteOptions = filterData.sites?.map(site => ({
        value: site.name,
        label: site.name,
    })) || [];

    return (
        <Main
            className="sites"
            title={<div>Sites</div>}
            hideSubHeader
            subHeaderTitle="Sites"
            subHeaderHasBackLink
            subHeaderBackLink="/"
            menuWidth={350}
            menuPlacement="right"
            toolbar={toolbar}
            showFooter={false}
            floatingHeader={false}
            footerContent=""
            floatingFooter={false}
            backLink={backLink}
            breadcrumb={navigation}>
            <div className="main-container sites-page">
                <h1 className="page-title">Sites</h1>
                {showInfoBanner && (
                    <div className="sites-info-banner">
                        <p>
                            Select a site from the list below to view the site information or to begin a booking request.
                            Any other disclaimer information that is currently used in the MTX Request Form,
                            or any other relevant information, can be displayed here.
                        </p>
                        <button
                            type="button"
                            className="info-banner-close"
                            onClick={() => setShowInfoBanner(false)}
                            aria-label="Close information banner">
                            <svg width="14" height="15" viewBox="0 0 14 15" fill="none" aria-hidden="true">
                                <path d="M13 1.5L1 13.5M1 1.5L13 13.5" stroke="#2B2D42" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    </div>
                )}
                {recentRequests && recentRequests.length > 0 && (
                    <div className="last-booked-sites-section">
                        <h3>Last Booked Sites</h3>
                        <div className="sites-table-container">
                            <Table
                                dataSource={recentRequests}
                                columns={recentRequestColumns}
                                rowKey="id"
                                className="sites-table"
                                pagination={false} />
                        </div>
                    </div>
                )}
                <div className="all-sites-header">
                    <h2>All Sites</h2>
                    {ADMIN_ACCESS_LEVELS.includes(userData?.role?.accessLevel) && (
                        <div className="ftc-actions" data-tour="sites-ftc">
                            <Button
                                type="primary"
                                onClick={() => setFtcReportVisible(true)}>
                                Fail to Close
                            </Button>
                            <Button
                                type="primary"
                                onClick={() => setFtcUpdateVisible(true)}>
                                Update Fail to Close
                            </Button>
                            {/* Project Guarding is hidden until the feature is in use.
                                Restore this button to bring it back. */}
                            {/* <Button
                                type="primary"
                                onClick={() => setProjectGuardingVisible(true)}>
                                Project Guarding
                            </Button> */}
                        </div>
                    )}
                </div>
                <div className="sites-content">
                    <div className="sites-filter-container">
                        <div className="filter-row">
                            <div className="search-input-wrapper">
                                <TextInput
                                    leftIcon={<SearchIcon />}
                                    placeholder="Search"
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)} />
                            </div>
                            <Select
                                mode="multiple"
                                placeholder="Site Type"
                                value={selectedSiteType}
                                onChange={setSelectedSiteType}
                                options={siteTypeOptions}
                                className="filter-select site-type-select"
                                allowClear
                                maxTagCount={0}
                                maxTagPlaceholder={(omittedValues) => (omittedValues.length === 1 ? omittedValues[0]?.label : `${omittedValues.length} Selected`)}
                                onDropdownVisibleChange={setSiteTypeDropdownOpen}
                                suffixIcon={(
                                    <div className={`dropdown-handle ${siteTypeDropdownOpen ? 'rotate' : ''}`}>
                                        <ChevronDownIcon w={20} />
                                    </div>
                                )} />
                            <Select
                                mode="multiple"
                                placeholder="Site"
                                value={selectedSite}
                                onChange={setSelectedSite}
                                options={siteOptions}
                                className="filter-select site-select"
                                allowClear
                                maxTagCount={0}
                                maxTagPlaceholder={(omittedValues) => (omittedValues.length === 1 ? omittedValues[0]?.label : `${omittedValues.length} Selected`)}
                                onDropdownVisibleChange={setSiteDropdownOpen}
                                suffixIcon={(
                                    <div className={`dropdown-handle ${siteDropdownOpen ? 'rotate' : ''}`}>
                                        <ChevronDownIcon w={20} />
                                    </div>
                                )} />
                            <Button type="link" className="clear-button" onClick={handleClearAll}>
                                Clear all
                            </Button>
                        </div>
                    </div>
                    {/* data-tour marks THIS container specifically: the Last
                        Booked Sites table above reuses `sites-table-container`,
                        so the class alone can't identify the All Sites table. */}
                    <div className="sites-table-container" data-tour="sites-all-table" ref={sitesTableRef}>
                        <Table
                            loading={isFetching}
                            dataSource={sitesData}
                            columns={tableColumns}
                            rowKey="id"
                            className="sites-table"
                            // Anchor for the feature tour's "first site" step. Applied
                            // by this table only, so `tr.tour-first-site-row` is unique
                            // even when Last Booked Sites is rendered above it.
                            rowClassName={(_: SiteData, index: number) => (index === 0 ? 'tour-first-site-row' : '')}
                            pagination={{
                                current: pagination.currentPage,
                                pageSize: pagination.pageSize,
                                total: pagination.total,
                                showSizeChanger: false,
                                itemRender,
                                showTotal: handleTotal,
                                onChange: handlePageChange,
                            }} />
                    </div>
                </div>
            </div>
            <FtcReportModal
                visible={ftcReportVisible}
                onClose={() => setFtcReportVisible(false)} />
            <FtcUpdateModal
                visible={ftcUpdateVisible}
                onClose={() => setFtcUpdateVisible(false)} />
            <ProjectGuardingModal
                visible={projectGuardingVisible}
                onClose={() => setProjectGuardingVisible(false)} />
        </Main>
    );
};

export default Sites;
